import React, { useRef, useState } from 'react';
import { colors } from '../utils/colors';

interface Props {
  onConfirm: () => void;
  disabled?: boolean;
  label?: string;
  confirmedLabel?: string;
}

const HANDLE = 58;
const PAD    = 5;

const SlideToSubmit: React.FC<Props> = ({
  onConfirm,
  disabled = false,
  label = 'Slide to confirm',
  confirmedLabel = 'Confirmed!',
}) => {
  const [dragX,     setDragX]     = useState(0);
  const [dragging,  setDragging]  = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const trackRef  = useRef<HTMLDivElement>(null);
  const startXRef = useRef(0);

  const maxDrag = () =>
    Math.max(0, (trackRef.current?.clientWidth ?? 300) - HANDLE - PAD * 2);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || confirmed) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragging(true);
    startXRef.current = e.clientX - dragX;
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragX(Math.max(0, Math.min(e.clientX - startXRef.current, maxDrag())));
  };

  const onPointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (dragX >= maxDrag() * 0.82) {
      setDragX(maxDrag());
      setConfirmed(true);
      onConfirm();
    } else {
      setDragX(0);
    }
  };

  const progress = maxDrag() > 0 ? dragX / maxDrag() : 0;
  const handleBg = disabled ? '#C8D0D7' : colors.primaryDark;

  return (
    <div
      ref={trackRef}
      style={{
        position: 'relative',
        height: HANDLE + PAD * 2,
        borderRadius: (HANDLE + PAD * 2) / 2,
        background: confirmed
          ? colors.success
          : disabled
          ? '#EEE3F7'
          : colors.primaryBg,
        overflow: 'hidden',
        userSelect: 'none',
        transition: 'background 0.4s ease',
      }}
    >
      {/* Fill that grows as you drag */}
      {!confirmed && !disabled && (
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: dragX + HANDLE + PAD * 2,
          background: `${colors.primaryDark}22`,
          borderRadius: 'inherit',
          transition: dragging ? 'none' : 'width 0.35s cubic-bezier(.34,1.56,.64,1)',
          pointerEvents: 'none',
        }} />
      )}

      {/* Label - centered in the full track width */}
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 15, fontWeight: 600,
        color: confirmed ? '#fff' : disabled ? colors.textLight : colors.textMuted,
        pointerEvents: 'none',
        transition: 'color 0.3s',
        letterSpacing: '0.1px',
      }}>
        {confirmed ? confirmedLabel : label}
      </div>

      {/* Handle */}
      {!confirmed && (
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{
            position: 'absolute',
            left: PAD + dragX,
            top: PAD,
            width: HANDLE, height: HANDLE,
            borderRadius: '50%',
            background: handleBg,
            boxShadow: disabled ? 'none' : '0 3px 12px rgba(68,40,85,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: disabled ? 'not-allowed' : 'grab',
            transition: dragging ? 'none' : 'left 0.35s cubic-bezier(.34,1.56,.64,1)',
            touchAction: 'none',
            zIndex: 1,
          }}
        >
          {/* >> icon */}
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            style={{ opacity: 0.55 + progress * 0.45, transition: dragging ? 'none' : 'opacity 0.2s' }}>
            <polyline points="5 17 10 12 5 7"/>
            <polyline points="12 17 17 12 12 7"/>
          </svg>
        </div>
      )}

      {/* Confirmed checkmark */}
      {confirmed && (
        <div style={{
          position: 'absolute', right: PAD, top: PAD,
          width: HANDLE, height: HANDLE, borderRadius: '50%',
          background: 'rgba(255,255,255,0.22)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          animation: 'splashPop 0.4s cubic-bezier(.34,1.56,.64,1) both',
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
        </div>
      )}
    </div>
  );
};

export default SlideToSubmit;
